const { push: Push } = require('connected-react-router');
const { connect } = require('react-redux');
const { context } = require('app-context');
const EditPage = require('../components/EditPage');
const { safeWarning } = require('assertions-simplified');
const GetQueryParams = require('utils/get-query-params');
const AuthConstants = require('services/auth-management/constants');
const RoutingTable = require('services/auth-management/routing-table');
const Continuation = require('services/auth-management/continuation');

const internals = {};
const selectors = context.selectors.all;
const actions   = context.actions;
const redux     = context.redux.hooks;

module.exports = connect(
    (state) => {

        const { id: loggedInSchoolId } = selectors.getCurrentSchool(state) || {};
        const { schoolId: signupSchoolId } = selectors.getSignupContext(state);

        const schoolId = loggedInSchoolId || signupSchoolId;
        const rolePermissions = selectors.getCurrentUserRolePermissions(state) || {};
        const signupDetails = selectors.getSignupDetails_forProfileEditPage(state);

        return {
            includePreferences: true,
            preferencesFieldsToShow: ['sms'],
            preferencesHeadersToShow: ['communications'],
            interestsLoading: selectors.getIsLoadingInterests(state),
            officesLoading: selectors.getIsLoadingOffices(state),
            yearsHiredLoading: selectors.getIsLoadingYearsHired(state),
            preferencesLoading: selectors.getIsLoadingPreferences(state),
            onCampusHousingList: selectors.getOnCampusHousingList(state, schoolId) || [],
            offCampusHousingList: selectors.getOffCampusHousingList(state, schoolId) || [],
            transfers: selectors.getBasicTransfers_bySchoolId(state, schoolId),
            signupDetails,
            majors: selectors.getMajorsList_bySchoolId(state, schoolId),
            departments: selectors.getDepartmentsList_bySchoolId(state, schoolId),
            yearsHiredList: selectors.getAllYearsHired(state),
            officesList: selectors.getAllOffices(state),
            incomingClassList: selectors.getIncomingClassList_bySchoolId(state, schoolId),
            graduatingClassList: selectors.getGraduatingClassList_bySchoolId(state, schoolId),
            badges: selectors.getBadgesForCurrentUser(state),
            isStudentRoleGroup: selectors.isStudentRoleGroup(state,selectors.getCurrentUserId(state)),
            isParent: selectors.isParentPermission(state),
            rolePermissions,
            role: selectors.getCurrentUserRole(state),
            schoolRoles: selectors.getSchoolRoles(state),
            isCompany: selectors.getSchoolIsCompany(state),
            isCommunity: selectors.getSchoolIsCommunity(state),
            isSchoolOnline: selectors.getSchoolIsOnline(state),
            requirePhoneForMfa: rolePermissions.mfa === AuthConstants.authFlow.mfaMethods.sms,
            signupContext: selectors.getSignupContext(state),
            fieldsPlaceholders: selectors.getFieldsPlaceholders(state, schoolId),
            useProfanityFilter: selectors.getShouldUseProfanityFilter(state, schoolId),
            defaultInterestId: selectors.getDefaultInterestId_bySchoolId(state, schoolId)
        };
    }, {
        onSubmit: (signupDetails, redirectPath = '/app/welcome') => async (dispatch) => { // eslint-disable-line

            const state = redux.getState();
            const { confirm } = GetQueryParams();
            const currentSchoolId = selectors.getCurrentSchoolId?.(state) || selectors.getCurrentSchool?.(state)?.id;
            const communityTarget = String(confirm) === 'true' ? Continuation.getCommunityTarget(currentSchoolId) : undefined;
            const heldContinuation = communityTarget ? JSON.stringify(Continuation.peek()) : undefined;
            redirectPath = communityTarget || redirectPath;

            const profileDiff = internals.withoutInterestFields(
                selectors.getUserProfileDiff_forUpdateProfile(state, signupDetails)
            );

            dispatch(actions.app.setShouldRefreshSearch({ discover: true, group: true }));

            const success = await dispatch(actions.profileManagement.updateProfile(profileDiff));

            // The action creator resolves undefined on failure
            if (!success) {
                throw new Error('Update profile failed');
            }

            if (communityTarget && JSON.stringify(Continuation.peek()) !== heldContinuation) {
                return;
            }

            if (RoutingTable.isPendingMfaStatus(success?.payload?.result?.status)) {
                RoutingTable.routePendingMfa({
                    currentContext: selectors.getLoginContext(state),
                    dispatch,
                    returnTo: redirectPath,
                    response: success.payload.result,
                    source: AuthConstants.authFlow.sources.profilePhoneUpdate,
                    updateContext: actions.auth.updateLoginContext
                });
                return;
            }

            const schoolName = selectors.getCurrentSchoolName(state);

            const toastMsg = String(confirm) === 'true' ? `Joined ${schoolName}` : 'Profile updated';

            if (communityTarget && JSON.stringify(Continuation.peek()) === heldContinuation) {
                Continuation.consume();
            }

            dispatch(Push(redirectPath));

            dispatch(actions.alerts.notification.push({
                message: toastMsg
            }));
        },
        showNotification: (msg) => actions.alerts.notification.push({ message: msg }),
        onSubmitNoRedirect: (signupDetails) => (dispatch) => { // eslint-disable-line

            const state = redux.getState();
            const profileDiff = internals.withoutInterestFields(
                selectors.getUserProfileDiff_forUpdateProfile(state, signupDetails)
            );

            dispatch(actions.app.setShouldRefreshSearch({ discover: true, group: true }));

            return dispatch(actions.profileManagement.updateProfile(profileDiff))
                .then((success) => {

                    const state = redux.getState();

                    const schoolName = selectors.getCurrentSchoolName(state);

                    const { confirm } = GetQueryParams();
                    const toastMsg = String(confirm) === 'true' ? `Joined ${schoolName}` : 'Profile updated';
                    const errMsg = String(confirm) === 'true' ? `Failed to join ${schoolName}` : 'Update profile failed';

                    // The action creator resolves undefined on failure
                    if (!success) {
                        throw new Error(errMsg);
                    }

                    if (RoutingTable.isPendingMfaStatus(success?.payload?.result?.status)) {
                        RoutingTable.routePendingMfa({
                            currentContext: selectors.getLoginContext(state),
                            dispatch,
                            returnTo: internals.getCurrentAppPath(),
                            response: success.payload.result,
                            source: AuthConstants.authFlow.sources.profilePhoneUpdate,
                            updateContext: actions.auth.updateLoginContext
                        });
                        return;
                    }

                    dispatch(actions.alerts.notification.push({
                        message: toastMsg
                    }));
                })
                .catch(safeWarning);
        },
        onInvalidSubmit: () => actions.alerts.notification.push({ message: 'Please fix invalid form fields' }),
        saveProfilePicture: (profilePictureDetails) => (dispatch, getState) => { // eslint-disable-line

            const profilePictureDiff = selectors.getUserProfilePictureDiff_forUpdateProfile(redux.getState(), profilePictureDetails);

            dispatch(actions.app.setShouldRefreshSearch({ discover: true, group: true }));

            return dispatch(actions.profileManagement.updateProfilePicture(profilePictureDiff))
                .then((success) => {

                    // The action creator resolves undefined on failure
                    if (!success) {
                        throw new Error('Update profile picture failed');
                    }

                    dispatch(actions.alerts.notification.push({
                        message: 'Profile picture updated'
                    }));

                    return success;
                })
                .catch(safeWarning);
        }
    }
)(EditPage);

internals.withoutInterestFields = (profileDiff = {}) => {

    const cleanedProfileDiff = { ...profileDiff };

    delete cleanedProfileDiff.interests;
    delete cleanedProfileDiff.passionInterests;

    return cleanedProfileDiff;
};

internals.getCurrentAppPath = () => `${window.location.pathname || ''}${window.location.search || ''}${window.location.hash || ''}`;
